import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const DevelopmentHomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-warm-background">
      {/* Header */}
      <header className="bg-space-cadet-primary text-white py-6">
        <div className="container mx-auto px-6">
          <div className="flex items-center space-x-4">
            <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-12 h-12" />
            <div>
              <h1 className="text-serif text-3xl font-bold">Cyrano Development Site</h1>
              <p className="text-cool-gray">Sophisticated AI-Powered Eulogy Generation</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        {/* Welcome Message for Co-Founder */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-6">
            <h2 className="text-serif text-4xl font-bold text-space-cadet-primary mb-4">
              🌹 Welcome to Cyrano Development
            </h2>
            <p className="text-xl text-cool-gray max-w-3xl mx-auto">
              This site updates automatically as new features are built.
              You're seeing the sophisticated conversational AI system take shape in real-time!
            </p>
          </div>

          <div className="bg-pink-lavender/10 border border-pink-lavender/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-space-cadet-primary mb-3">
              📧 For Co-Founder Review:
            </h3>
            <p className="text-cool-gray">
              This development site automatically deploys within minutes of any code changes.
              Check back regularly to see progress on our sophisticated features that will
              set us apart from any competitors in the eulogy generation space.
            </p>
          </div>
        </motion.div>

        {/* Current System Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Existing Working Features */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-serif text-2xl font-bold text-space-cadet-primary mb-6">
              ✅ Working Features (Preserved)
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="text-green-500 text-xl">✅</span>
                <div>
                  <p className="font-semibold">Beautiful Landing Page</p>
                  <p className="text-sm text-cool-gray">Lottie animations, perfect scroll experience</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-green-500 text-xl">✅</span>
                <div>
                  <p className="font-semibold">15-Question Conversation System</p>
                  <p className="text-sm text-cool-gray">OpenAI GPT-4o-mini integration working</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-green-500 text-xl">✅</span>
                <div>
                  <p className="font-semibold">Export & Sharing Features</p>
                  <p className="text-sm text-cool-gray">Copy, email, social media sharing</p>
                </div>
              </div>
              <div className="mt-6">
                <a
                  href="/conversation-interface.html"
                  className="inline-block bg-space-cadet-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-space-cadet-secondary transition-colors duration-200"
                >
                  Try Working System
                </a>
              </div>
            </div>
          </motion.div>

          {/* Advanced Features in Development */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-serif text-2xl font-bold text-space-cadet-primary mb-6">
              🚧 Advanced Features (Building)
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="text-yellow-500 text-xl">🔧</span>
                <div>
                  <p className="font-semibold">Sophisticated Conversational AI</p>
                  <p className="text-sm text-cool-gray">Emotional intelligence, adaptive questioning</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-yellow-500 text-xl">🔧</span>
                <div>
                  <p className="font-semibold">Family Collaboration System</p>
                  <p className="text-sm text-cool-gray">Real-time, 5-member limit, live updates</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-yellow-500 text-xl">🔧</span>
                <div>
                  <p className="font-semibold">Professional Reference Library</p>
                  <p className="text-sm text-cool-gray">5 years of professional eulogies for AI enhancement</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-blue-500 text-xl">⚡</span>
                <div>
                  <p className="font-semibold">Background Processing</p>
                  <p className="text-sm text-cool-gray">Job queues, email notifications, media processing</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Architecture Overview */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-serif text-2xl font-bold text-space-cadet-primary mb-6 text-center">
            🏗️ Sophisticated Architecture
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-blue rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white text-2xl">⚛️</span>
              </div>
              <h4 className="font-semibold text-space-cadet-primary mb-2">React Frontend</h4>
              <p className="text-sm text-cool-gray">Modern UI with real-time collaboration</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-blue rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white text-2xl">⚙️</span>
              </div>
              <h4 className="font-semibold text-space-cadet-primary mb-2">Node.js Backend</h4>
              <p className="text-sm text-cool-gray">AI processing, job queues, complex logic</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-blue rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white text-2xl">🗄️</span>
              </div>
              <h4 className="font-semibold text-space-cadet-primary mb-2">Supabase Database</h4>
              <p className="text-sm text-cool-gray">Advanced schema, real-time updates</p>
            </div>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          className="bg-space-cadet-primary text-white rounded-xl shadow-lg p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3 className="text-serif text-2xl font-bold mb-6 text-center">
            🔗 Quick Access
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <a
              href="/conversation-interface.html"
              className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-center transition-colors duration-200"
            >
              <div className="text-2xl mb-2">💬</div>
              <div className="font-semibold">Current System</div>
              <div className="text-xs text-cool-gray">Working conversation interface</div>
            </a>
            <Link
              to="/conversation"
              className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-center transition-colors duration-200"
            >
              <div className="text-2xl mb-2">🤖</div>
              <div className="font-semibold">Advanced AI</div>
              <div className="text-xs text-cool-gray">Sophisticated conversation system</div>
            </Link>
            <Link
              to="/family/demo"
              className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-center transition-colors duration-200"
            >
              <div className="text-2xl mb-2">👨‍👩‍👧‍👦</div>
              <div className="font-semibold">Family Collaboration</div>
              <div className="text-xs text-cool-gray">Real-time family features</div>
            </Link>
            <a
              href="/api/health"
              className="bg-white/10 hover:bg-white/20 rounded-lg p-4 text-center transition-colors duration-200"
            >
              <div className="text-2xl mb-2">📊</div>
              <div className="font-semibold">API Status</div>
              <div className="text-xs text-cool-gray">Backend health check</div>
            </a>
          </div>
        </motion.div>

        {/* Latest Updates */}
        <motion.div
          className="bg-gradient-to-r from-pink-lavender/10 to-cool-gray/10 rounded-xl p-8 mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <h3 className="text-serif text-2xl font-bold text-space-cadet-primary mb-4 text-center">
            📈 Latest Development Progress
          </h3>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-cool-gray mb-4">
              <strong>Current Focus:</strong> Building the sophisticated conversational AI engine
              with emotional intelligence and dynamic conversation flows.
            </p>
            <p className="text-sm text-cool-gray">
              Last Updated: {new Date().toLocaleDateString()} -
              Vercel deployment pipeline active, automatic updates enabled
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default DevelopmentHomePage